import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    memberId: Number,
    userId: Number,
    projectId: Number,
    resendInviteUrl: String,
    destroyUrl: String,
    makeOwnerUrl: String,
    transferOwnershipUrl: String,
    makeGroupAdminUrl: String,
    removeGroupAdminUrl: String,
    csrfToken: String,
    updatePermissionsUrl: String,
    updateFinanceApprovalsUrl: String,
    updateLabelUrl: String,
    memberName: String,
    phase: String,
    designPermissions: Array,
    constructionPermissions: Array
  }

  static targets = ["ownerBadge", "permissionsModal", "permissionsDialog", "permissionCheckbox", "permissionsSave", "dashboardWarning", "labelSelect", "labelBadge", "pmPermissionsModal", "pmPermCheckbox", "pmBadge"]

  resendInvite(event) {
    event.preventDefault()
    fetch(this.resendInviteUrlValue, {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      }
    })
      .then(response => response.json())
      .then(data => {
        this.showFlash(data.message || "Invitation resent.", "notice")
      })
      .catch(() => {
        this.showFlash("Failed to resend invitation.", "alert")
      })
  }

  removeMember(event) {
    event.preventDefault()

    fetch(this.destroyUrlValue, {
      method: "DELETE",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.status === "ok") {
          this.element.remove()
          this.showFlash(data.message || "Member removed.", "notice")
        } else {
          this.showFlash(data.message || "Could not remove member.", "alert")
        }
      })
      .catch(() => {
        this.showFlash("Failed to remove member.", "alert")
      })
  }

  makeProjectOwner(event) {
    event.preventDefault()

    fetch(this.makeOwnerUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      },
      body: new URLSearchParams({ user_id: this.userIdValue })
    })
      .then(response => {
        if (response.ok) {
          // Hide all owner badges, then show this member's badge
          document.querySelectorAll('[data-team-member-target="ownerBadge"]').forEach(badge => {
            badge.classList.add("hidden")
          })
          if (this.hasOwnerBadgeTarget) {
            this.ownerBadgeTarget.classList.remove("hidden")
          }

          // Close the confirmation modal
          const modal = this.element.querySelector('[data-modal-id-value^="confirm-make-owner-"]')
          if (modal) {
            const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
            if (modalController) modalController.close()
          }

          this.showFlash("Project owner updated.", "notice")
        } else {
          this.showFlash("Could not update project owner.", "alert")
        }
      })
      .catch(() => {
        this.showFlash("Failed to update project owner.", "alert")
      })
  }

  transferOwnership(event) {
    event.preventDefault()

    fetch(this.transferOwnershipUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      },
      body: new URLSearchParams({ user_id: this.userIdValue })
    })
      .then(response => response.json())
      .then(data => {
        if (data.status === "ok") {
          this.showFlash(data.message || "Ownership transferred successfully.", "notice")
          setTimeout(() => window.location.reload(), 800)
        } else {
          this.showFlash(data.message || "Could not transfer ownership.", "alert")
        }
      })
      .catch(() => {
        this.showFlash("Failed to transfer ownership.", "alert")
      })
  }

  makeGroupAdmin(event) {
    event.preventDefault()

    fetch(this.makeGroupAdminUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      },
      body: new URLSearchParams({ user_id: this.userIdValue })
    })
      .then(response => {
        if (response.ok) {
          this.showFlash("Group admin assigned.", "notice")
          window.location.reload()
        } else {
          this.showFlash("Could not assign group admin.", "alert")
        }
      })
      .catch(() => {
        this.showFlash("Failed to assign group admin.", "alert")
      })
  }

  removeGroupAdmin(event) {
    event.preventDefault()

    fetch(this.removeGroupAdminUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      },
      body: new URLSearchParams({ user_id: this.userIdValue })
    })
      .then(response => {
        if (response.ok) {
          this.showFlash("Group admin removed.", "notice")
          window.location.reload()
        } else {
          this.showFlash("Could not remove group admin.", "alert")
        }
      })
      .catch(() => {
        this.showFlash("Failed to remove group admin.", "alert")
      })
  }

  openPermissions(event) {
    event.preventDefault()
    if (this.hasPermissionsModalTarget) {
      this.permissionsModalTarget.classList.remove("hidden")
      this.permissionSelectionChanged()
    }
  }

  // Keeps Save disabled when nothing is checked (an all-unchecked save would be
  // a total lockout — the server rejects it too) and shows the Dashboard warning
  // when the project's landing section is unchecked.
  permissionSelectionChanged() {
    const anyChecked = this.permissionCheckboxTargets.some(cb => cb.checked)
    if (this.hasPermissionsSaveTarget) {
      this.permissionsSaveTarget.disabled = !anyChecked
    }
    if (this.hasDashboardWarningTarget) {
      const dashboard = this.permissionCheckboxTargets.find(cb => cb.value === "dashboard")
      this.dashboardWarningTarget.classList.toggle("hidden", !dashboard || dashboard.checked)
    }
  }

  closePermissions() {
    if (this.hasPermissionsModalTarget) {
      this.permissionsModalTarget.classList.add("hidden")
    }
  }

  permissionsBackdropMousedown(event) {
    this._permissionsBackdropClicked = event.target === this.permissionsModalTarget
  }

  closePermissionsOnBackdrop(event) {
    if (this._permissionsBackdropClicked && event.target === this.permissionsModalTarget) {
      this.closePermissions()
    }
    this._permissionsBackdropClicked = false
  }

  savePermissions(event) {
    event.preventDefault()

    const selected = this.permissionCheckboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value)

    if (selected.length === 0) return

    const fieldName = this.phaseValue === "dev"
      ? "design_sections_access_permitted"
      : "construction_sections_access_permitted"

    fetch(this.updatePermissionsUrlValue, {
      method: "PATCH",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      },
      body: new URLSearchParams({ [fieldName]: selected.join(",") })
    })
      .then(response => {
        if (response.ok) {
          this.closePermissions()
          this.showFlash("Permissions updated.", "notice")
        } else {
          return response.json()
            .then(data => this.showFlash(data.message || "Could not update permissions.", "alert"))
            .catch(() => this.showFlash("Could not update permissions.", "alert"))
        }
      })
      .catch(() => {
        this.showFlash("Failed to update permissions.", "alert")
      })
  }

  // ── PM Permissions (stakeholder finance-approval grants) ──────────────
  // The project owner picks which finance areas this stakeholder member may
  // approve (fee schedule, pay requests, PCOs, COs). Approve-side rights
  // only — no owner editing powers.

  openPmPermissions(event) {
    event.preventDefault()
    if (this.hasPmPermissionsModalTarget) {
      this.pmPermissionsModalTarget.classList.remove("hidden")
    }
  }

  closePmPermissions() {
    if (this.hasPmPermissionsModalTarget) {
      this.pmPermissionsModalTarget.classList.add("hidden")
    }
  }

  pmPermissionsBackdropMousedown(event) {
    this._pmBackdropClicked = event.target === this.pmPermissionsModalTarget
  }

  closePmPermissionsOnBackdrop(event) {
    if (this._pmBackdropClicked && event.target === this.pmPermissionsModalTarget) {
      this.closePmPermissions()
    }
    this._pmBackdropClicked = false
  }

  savePmPermissions(event) {
    event.preventDefault()

    const selected = this.pmPermCheckboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value)

    fetch(this.updateFinanceApprovalsUrlValue, {
      method: "PATCH",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      },
      body: new URLSearchParams({ finance_approvals: selected.join(",") })
    })
      .then(response => {
        if (response.ok) {
          this.closePmPermissions()
          if (this.hasPmBadgeTarget) {
            this.pmBadgeTarget.classList.toggle("hidden", selected.length === 0)
            this.pmBadgeTarget.classList.toggle("inline-flex", selected.length > 0)
          }
          this.showFlash("PM permissions updated.", "notice")
        } else {
          return response.json()
            .then(data => this.showFlash(data.message || "Could not update PM permissions.", "alert"))
            .catch(() => this.showFlash("Could not update PM permissions.", "alert"))
        }
      })
      .catch(() => {
        this.showFlash("Failed to update PM permissions.", "alert")
      })
  }

  // Assign a single responsibility label to this member (CPFL / company 1406).
  // Selecting "None" (value "n/a") clears it. Updates the member badge and the
  // Responsibilities card in place — no full page reload.
  saveLabel(event) {
    event.preventDefault()

    const labelId = this.hasLabelSelectTarget ? this.labelSelectTarget.value : "n/a"

    fetch(this.updateLabelUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      },
      body: new URLSearchParams({ user_id: this.userIdValue, label_id: labelId })
    })
      .then(response => (response.ok ? response.json() : Promise.reject(response)))
      .then(data => {
        if (this.hasLabelBadgeTarget) {
          this.labelBadgeTarget.innerHTML = data.label
            ? `<span class="shrink-0 rounded-full border border-blue-300 text-blue-400 bg-white" style="font-size: 14px; padding: 1px 8px;">${this._escapeHtml(data.label)}</span>`
            : ""
        }

        if (data.card_html) {
          const card = document.getElementById("responsibilities-card")
          if (card) card.outerHTML = data.card_html
        }

        const modal = this.element.querySelector('[data-modal-id-value^="edit-member-label-"]')
        if (modal) {
          const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
          if (modalController) modalController.close()
        }

        this.showFlash("Responsibility updated.", "notice")
      })
      .catch(() => {
        this.showFlash("Could not update responsibility.", "alert")
      })
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  showFlash(message, type) {
    const cssType = type === "notice" ? "notice" : "alert"
    const iconPath = cssType === "notice"
      ? '<path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>'
      : '<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>'

    const flash = document.createElement("div")
    flash.className = `flash-toast-${cssType}`
    flash.setAttribute("data-controller", "flash")
    flash.setAttribute("data-flash-dismiss-after-value", "5000")
    flash.setAttribute("role", "alert")
    flash.innerHTML = `
      <div class="shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">${iconPath}</svg>
      </div>
      <p class="flex-1 text-sm">${message}</p>
    `
    document.body.appendChild(flash)
  }
}
